import { useState, useEffect } from 'react';
import { useNotification } from '../../../../contexts/CustomNotificationContext.jsx';
import { buttonStyle } from '../../../../libs/sharedStyles.js';
import { primaryButtonClasses } from '../../../../libs/sharedClassNames.js';
import HotglueConfig from '@hotglue/widget';
import { useHotglue } from '@hotglue/widget';
import { useAuthContext } from '../../../../contexts/AuthContext.jsx';
import { activateHgConnection } from '../../../../libs/fetch.js';
import Loading from '../../../Loading.jsx';

export default function OnboardingFormHotglue(props) {
  const { getHgJwt } = useAuthContext();
  const [hgJwt, setHgJwt] = useState(null);
  const [tenant, setTenant] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJwt = async () => {
      const token = await getHgJwt();
      if (token?.jwt) {
        setHgJwt(token.jwt);
        setTenant(token.tenant);
      }
      setLoading(false);
    };
    fetchJwt();
  }, []);

  if (loading) return <Loading />;
  if (!hgJwt) return <div className="mt-5 text-red-600">Error initializing connection. Please try again later.</div>;

  return (
    <HotglueConfig
      config={{
        apiKey: 'F30ISCXzyQeuflGJkifLay6EyRXBoHN2BbZyOvC9',
        envId: 'prod.hotglue.muffindata.com',
        jwtToken: hgJwt,
      }}
    >
      <HotglueForm {...props} jwt={hgJwt} tenant={tenant} />
    </HotglueConfig>
  );
}


function HotglueForm(props) {
  const { jwt, tenant, connection_id, tap, target, defaultValues, flow_id } = props;
  const { sendNotification } = useNotification();
  const { link, setOptions, getAvailableSources, getLinkedSources, getAvailableTargets, getLinkedTargets } = useHotglue();

  const [components, setComponents] = useState([]);
  const [source, setSource] = useState(null);
  const [linked, setLinked] = useState('');
  const [connectUiParams, setConnectUiParams] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const dv = defaultValues ?? {};

  useEffect(() => {
    const init = async () => {
      try {
        setOptions({ jwtToken: jwt });

        let available, src;
        if (tap) {
          available = await getAvailableSources();
          src = available.find(source => source.tap === tap);
        } else {
          available = await getAvailableTargets();
          src = available.find(source => source.target === target);
        }

        setSource(src);
        const uiParams = src.connect_ui_params ?? {};
        const params = Object.keys(uiParams).map((k) => {
          const comp = { ...uiParams[k], id: k };
          if (comp.type === 'select') {
            comp.options = comp.options.map((opt) => ({ key: opt.name, value: opt.value, label: opt.name }));
            comp.defaultOption = comp.options.find(opt => opt.key === dv[comp.id]);
          }
          return comp;
        });
        setComponents(params);

        let linkedData;
        if (tap) {
          linkedData = await getLinkedSources(flow_id, tenant);
        } else {
          linkedData = await getLinkedTargets(flow_id, tenant);
        }
        if (linkedData?.[0]?.config) {
          setLinked(linkedData[0].config);
        }
      } catch (e) {
        console.error("Failed to load hotglue components:", e);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const buttonText = () => {
    const t = source?.type;
    const exists = !!linked;
    switch (true) {
      case t === 'oauth' && connecting: return 'Authorizing...';
      case t === 'oauth' && exists: return 'Reauthorize';
      case t === 'oauth': return 'Authorize';
      case connecting && exists: return 'Updating...';
      case connecting: return 'Connecting...';
      case exists: return 'Update';
      default: return 'Connect';
    }
  };

  const onChange = (e) => {
    setConnectUiParams({ ...connectUiParams, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setConnecting(true);
    link(tenant, flow_id, tap ?? target, false, {
      jwtToken: jwt,
      isTarget: !tap,
      checkLink: false,
      listener: {
        onPopupClose: () => {},
        onSourceLinkFailed: (_, __, ___, message) => {
          console.log('onSourceLinkFailed', message);
          sendNotification('Linking Failed!', message, true);
          setConnecting(false);
        },
        onSourceLinkCanceled: () => {
          sendNotification('Linking Cancelled', "Try again when you're ready.", true);
          setConnecting(false);
        },
        onSourceLinked: () => {
          sendNotification('Successfully Linked!', "We'll begin pulling data shortly.");
          activateConnection();
          setConnecting(false);
        },
        onTargetLinkFailed: (_, __, ___, message) => {
          sendNotification('Linking Failed!', message, true);
          setConnecting(false);
        },
        onTargetLinkCanceled: () => {
          sendNotification('Linking Cancelled', "Try again when you're ready.", true);
          setConnecting(false);
        },
        onTargetLinked: () => {
          sendNotification('Successfully Linked!', 'Your connection will be confirmed shortly.');
          setConnecting(false);
        },
      },
      connectValues: linked ? connectUiParams : { ...dv, ...connectUiParams },
    });
  };

  const activateConnection = async () => {
    const { data, error } = await activateHgConnection(connection_id);
  };

  return (
    <div className="flow-root mt-5">
      {loading ? <Loading /> :
        <form className="space-y-6" onSubmit={handleSubmit}>
          {components.map((comp) => (
            <div key={comp.id}>
              <label htmlFor={comp.id} className="block text-md font-medium text-gray-700 pt-8">{comp.label}</label>
              {comp.description && <p className="text-sm text-gray-500">{comp.description}</p>}
              {comp.type === 'select' ? (
                <select
                  id={comp.id}
                  name={comp.id}
                  defaultValue={dv[comp.id]}
                  onChange={onChange}
                >
                  {comp.options.map((opt) => (
                    <option key={opt.key} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={comp.type}
                  name={comp.id}
                  id={comp.id}
                  autoComplete="off"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 med:text-med px-3 py-2"
                  placeholder={comp.type === 'password' && linked[comp.id] ? "(update existing)" : linked[comp.id] ?? comp.label}
                  value={dv[comp.id]}
                  onChange={onChange}
                />
              )}
            </div>
          ))}
          <button type="submit" className={primaryButtonClasses} style={buttonStyle}>
            {buttonText()}
          </button>
        </form>
      }
    </div>
  );
}
